import { useState, useEffect } from 'react';

import { flowchartLogger } from 'src/lib/logger';

import { isHeaderValid } from '../../components/HeaderInput';
import HTTPInput from '../../components/HTTPInput';
import NodeControlForm from '../NodeControlForm';

import type { HTTPData, Header, NodeControlsProps } from '../../dataProvider/types';

interface HTTPControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: HTTPData;
}

export default function HTTPControls({ updateNodeData, data }: HTTPControlsProps): React.ReactElement {
  const [url, setUrl] = useState<string>(data.url ?? '');
  const [method, setMethod] = useState<'get' | 'post'>(data.method ?? 'get');
  const [headers, setHeaders] = useState<Header[]>(data.headers ?? []);
  const [sslVerify, setSSLVerify] = useState<boolean>(data.sslVerify ?? true);

  const onUpdateHTTPInputData = ({ url, method, headers, sslVerify }: HTTPData): void => {
    flowchartLogger.debug('HTTPControls: onUpdateHTTPInputData', { url, method, headers, sslVerify });
    setUrl(url);
    setMethod(method);
    setHeaders(headers);
    setSSLVerify(sslVerify);
  };

  const isFormDisabled = (): boolean => {
    const isDisabled = url === '' || headers.some((header) => !isHeaderValid(header));
    flowchartLogger.debug('HTTPControls: isFormDisabled', { isDisabled });
    return isDisabled;
  };

  const onHTTPDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('HTTPControls: onHTTPDataUpdate', { url, method, headers, sslVerify });
    if (url === undefined || url === '') {
      flowchartLogger.debug('HTTPControls: url is undefined or empty. Update cancelled.');
      return;
    }
    if (headers.some((header) => !isHeaderValid(header))) {
      flowchartLogger.debug('HTTPControls: at least one header is invalid. Update cancelled.');
      return;
    }
    updateNodeData({ url, method, headers, sslVerify });
  };

  useEffect(() => {
    flowchartLogger.debug('HTTPControls: props changed', { url });
    updateNodeData({ url, method, headers, sslVerify });
  }, [url, method, headers, sslVerify, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onHTTPDataUpdate}
      isDisabled={isFormDisabled()}
      data={data}
      updateNodeData={updateNodeData}
    >
      <HTTPInput updateHTTPInputData={onUpdateHTTPInputData} data={{ url, method, headers, sslVerify }} />
    </NodeControlForm>
  );
}
